import { Event, Person } from '@mui/icons-material';
import { Box, Divider } from '@mui/material';
import moment from 'moment/moment';
import { useMemo } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { EmployeeCalendar } from '~/components/EmployeeCalendar';
import {
  AnalyticsReportEmployeeScope,
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportTypes,
  useAbsencesReportData,
} from '~/data/reports';
import {
  ReportDatasetLayout,
  ReportDatasetRowCount,
  ReportDatasetSelector,
  ReportDateRangeSelector,
  ReportDateRangeSelectorMode,
  ReportViewLayout,
  useReportDataset,
  useReportNavigateToView,
} from '~/scenes/PortalModules/Analytics/common';
import { PersonnelGroupsFilters } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarReport/PersonnelGroupsFilters';
import { ShowEmployeesModeSelector } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarReport/ShowEmployeesModeSelector';
import { useAbsenceCalendarReport } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarReport/useAbsenceCalendarReport';
import { useSxStyles } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarReport/useStyles';

export const AbsenceCalendarReport = () => {
  const { t } = useTranslation();

  const sxStyles = useSxStyles();

  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const loggedInEmployeeId = useLoggedInEmployeeId();

  const defaultReportSelection = useMemo(() => {
    const today = moment();

    const dateRange = {
      startDate: today.clone().subtract(1, 'month').toDate(),
      endDate: today.clone().add(2, 'months').toDate(),
    };

    return {
      reportType: {
        type: AnalyticsReportTypes.Team,
        role: AnalyticsReportRoleScope.AltManager,
        employeeId: loggedInEmployeeId,
        scope: AnalyticsReportScope.Direct,
      },
      params: {
        ...dateRange,
        empScope: AnalyticsReportEmployeeScope.AllEmployees,
      },
    };
  }, [loggedInEmployeeId]);

  const reportDatasetProps = useReportDataset({
    selection: defaultReportSelection,
    config: {
      options: {
        types: [
          AnalyticsReportTypes.Team,
          AnalyticsReportTypes.HR,
          AnalyticsReportTypes.Unit,
        ],
        roles: [
          AnalyticsReportRoleScope.Manager,
          AnalyticsReportRoleScope.DeputyManager,
        ],
      },
    },
  });

  const { reportRange } = useMemo(() => {
    const reportRange = {
      startDate:
        reportDatasetProps.reportSelection?.params?.startDate ??
        defaultReportSelection.params.startDate,
      endDate:
        reportDatasetProps.reportSelection?.params?.endDate ??
        defaultReportSelection.params.endDate,
    };

    return {
      reportRange,
    };
  }, [reportDatasetProps, defaultReportSelection]);

  // Load data
  const {
    data,
    isLoading: isLoadingReportData,
    failedToLoad,
  } = useAbsencesReportData(reportDatasetProps.reportSelection);

  // Process data
  const {
    showEmployeesModeSelector,
    personnelGroupsFilters,
    counts,
    calendarProps,
    isLoading: isLoadingPublicHolidays,
  } = useAbsenceCalendarReport({
    data,
    ...reportRange,
  });

  return (
    <ReportViewLayout
      heading={t('laAbsCal')}
      slots={{
        toolbar: {
          main: (
            <Box sx={sxStyles.reportToolbarWrapper}>
              <ReportDatasetLayout
                showHeading={true}
                sx={sxStyles.datasetLayout}
              >
                <ReportDatasetSelector
                  {...reportDatasetProps}
                  isLoading={isLoadingReportData || isLoadingPublicHolidays}
                  failedToLoad={failedToLoad}
                  renderCurrentParams={({ params }) => {
                    return (
                      <>{`${moment(params?.startDate).format('L')} - ${moment(
                        params?.endDate,
                      ).format('L')}`}</>
                    );
                  }}
                  renderParamsFields={({ params, onChange }) => {
                    return (
                      <ReportDateRangeSelector
                        startDate={params.startDate}
                        endDate={params.endDate}
                        onRangeChange={(startDate, endDate) =>
                          onChange({
                            ...params,
                            startDate: startDate ?? params.startDate,
                            endDate: endDate ?? params.endDate,
                          })
                        }
                        mode={ReportDateRangeSelectorMode.DateRange}
                      />
                    );
                  }}
                />
                <ReportDatasetRowCount
                  count={counts.employees}
                  description={t('laTotalRecords')}
                  icon={<Person />}
                />
                <ReportDatasetRowCount
                  count={counts.absences}
                  description={t('laAbsences')}
                  icon={<Event />}
                />
              </ReportDatasetLayout>
              <Box sx={sxStyles.filtersLayout}>
                <PersonnelGroupsFilters {...personnelGroupsFilters} />
                <Divider
                  orientation={'vertical'}
                  sx={sxStyles.verticalDivider}
                />
                <ShowEmployeesModeSelector {...showEmployeesModeSelector} />
              </Box>
            </Box>
          ),
        },
      }}
    >
      <EmployeeCalendar
        containerWidth={500}
        startDate={reportRange.startDate}
        endDate={reportRange.endDate}
        {...calendarProps}
        onEmployeeClick={goToEmployeeBasicDataView}
        isLoading={isLoadingReportData || isLoadingPublicHolidays}
      />
    </ReportViewLayout>
  );
};
